/**
 * Modal creates popup which on mobile layout fills the entire visible page.
 *
 * Example:
 * <Parent>
 *   <Modal id="UniqueIdForThisModal" isOpen={this.state.modalIsOpen} onClose={handleClose}>
 *     <FormX />
 *   </Modal>
 * </Parent>
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { intlShape, injectIntl } from 'react-intl';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { ReactComponent as CloseIcon } from '../../assets/icons/close-regular.svg';

import { Button } from '..';
import { manageDisableScrolling } from '../../ducks/UI.duck';

import css from './Modal.module.css';

const KEY_CODE_ESCAPE = 27;

export class ModalComponent extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleClick = this.handleClick.bind(this);
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling } = this.props;

    if (onManageDisableScrolling) {
      onManageDisableScrolling(id, isOpen);
    }

    document.body.addEventListener('keyup', this.handleBodyKeyUp);
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    if (nextProps.isOpen !== isOpen && onManageDisableScrolling) {
      onManageDisableScrolling(id, nextProps.isOpen);
    }

    if (nextProps.isOpen) {
      document.addEventListener('mousedown', this.handleClick);
    } else {
      document.removeEventListener('mousedown', this.handleClick);
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    document.body.removeEventListener('keyup', this.handleBodyKeyUp);

    if (onManageDisableScrolling) {
      onManageDisableScrolling(id, false);
    }
  }

  handleBodyKeyUp(event) {
    const { isOpen } = this.props;
    if (event.keyCode === KEY_CODE_ESCAPE && isOpen) {
      this.handleClose(event);
    }
  }

  handleClose(event) {
    const { id, onClose, onManageDisableScrolling } = this.props;

    if (onManageDisableScrolling) {
      onManageDisableScrolling(id, false);
    }
    onClose(event);
  }

  handleClick(event) {
    const { dontCloseOnOutsideClick } = this.props;

    if (dontCloseOnOutsideClick || !this.node || this.node.contains(event.target)) {
      return;
    }

    this.handleClose(event);
  }

  render() {
    const {
      children,
      className,
      scrollLayerClassName,
      containerClassName,
      contentClassName,
      lightCloseButton,
      customCloseIcon,
      customCloseButton,
      intl,
      isClosedClassName,
      isOpen,
      noCloseBtn,
    } = this.props;

    const closeModalMessage = intl.formatMessage({ id: 'Modal.closeModal' });
    const closeButtonClasses =
      customCloseButton ||
      classNames(css.close, {
        [css.closeLight]: lightCloseButton,
      });
    const closeButtonIconClasses = customCloseIcon || css.closeIcon;
    const closeBtn =
      isOpen && !noCloseBtn ? (
        <Button
          onClick={this.handleClose}
          rootClassName={closeButtonClasses}
          title={closeModalMessage}
        >
          <CloseIcon className={closeButtonIconClasses} />
        </Button>
      ) : null;

    // Modal uses given styles to wrap child components.
    // If props doesn't contain isClosedClassName, styles default to css.isClosed
    // This makes it possible to create ModalInMobile on top of Modal where style modes are:
    // visible, hidden, or none (ModalInMobile's children are always visible on desktop layout.)
    const modalClass = isOpen ? css.isOpen : isClosedClassName;
    const classes = classNames(modalClass, className);
    const scrollLayerClasses = scrollLayerClassName || css.scrollLayer;
    const containerClasses = containerClassName || css.container;
    return (
      <div className={classes}>
        <div className={scrollLayerClasses}>
          <div
            className={containerClasses}
            ref={node => {
              this.node = node;
            }}
          >
            {closeBtn}
            <div className={classNames(contentClassName || css.content)}>{children}</div>
          </div>
        </div>
      </div>
    );
  }
}

ModalComponent.defaultProps = {
  children: null,
  className: null,
  scrollLayerClassName: null,
  containerClassName: null,
  contentClassName: null,
  lightCloseButton: false,
  customCloseButton: null,
  customCloseIcon: null,
  isClosedClassName: css.isClosed,
  isOpen: false,
  onClose: null,
  dontCloseOnOutsideClick: false,
};

const { bool, func, node, string } = PropTypes;

ModalComponent.propTypes = {
  children: node,
  className: string,
  scrollLayerClassName: string,
  containerClassName: string,
  contentClassName: string,
  lightCloseButton: bool,
  customCloseButton: string,
  customCloseIcon: string,
  id: string.isRequired,
  intl: intlShape.isRequired,
  isClosedClassName: string,
  isOpen: bool,
  onClose: func,
  onManageDisableScrolling: func.isRequired,
  dontCloseOnOutsideClick: bool,
};

// TODO: Cleanup other components where we are prop-drilling onManageDisableScrolling (CheckoutPage, Authentication, etc)
const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

export default compose(
  injectIntl,
  connect(
    null,
    mapDispatchToProps
  )
)(ModalComponent);
